import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { format } from 'date-fns';

const { FiSun, FiCloud, FiCloudRain, FiCloudSnow, FiWind, FiDroplet, FiThermometer, FiEye } = FiIcons;

function WeatherWidget({ preview = false }) {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchWeather = async () => {
      try {
        setLoading(true);
        // Updated API call to ensure we get Fahrenheit for NYC
        // Using latitude and longitude for Manhattan, NY
        const response = await fetch(
          'https://api.open-meteo.com/v1/forecast?latitude=40.7128&longitude=-74.0060&current=temperature_2m,weathercode,wind_speed_10m,relative_humidity_2m&daily=temperature_2m_max,temperature_2m_min,weathercode&temperature_unit=fahrenheit&wind_speed_unit=mph&timezone=America/New_York&forecast_days=5'
        );

        if (!response.ok) {
          throw new Error('Failed to fetch weather data');
        }

        const data = await response.json();
        console.log("Weather data:", data); // For debugging
        setWeather(data);
      } catch (err) {
        console.error("Weather fetch error:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchWeather();
  }, []);

  const getWeatherIcon = (code) => {
    if (code <= 3) return FiSun;
    if (code <= 48) return FiCloud;
    if (code <= 67) return FiCloudRain;
    if (code <= 77) return FiCloudSnow;
    return FiCloud;
  };

  const getWeatherDescription = (code) => {
    if (code === 0) return 'Clear sky';
    if (code <= 3) return 'Partly cloudy';
    if (code <= 48) return 'Foggy';
    if (code <= 67) return 'Rainy';
    if (code <= 77) return 'Snowy';
    return 'Cloudy';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-blue"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-400 p-8">
        <p>Unable to load weather data</p>
        <p className="text-sm text-gray-500">{error}</p>
      </div>
    );
  }

  if (!weather) return null;

  const currentWeather = weather.current;
  const dailyWeather = weather.daily;

  if (preview) {
    return (
      <div className="max-w-md mx-auto">
        <div className="bg-dark-bg rounded-lg p-6 border border-dark-border">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-xl font-semibold">New York City</h3>
              <p className="text-gray-400">Right now</p>
            </div>
            <SafeIcon
              icon={getWeatherIcon(currentWeather.weathercode)}
              className="text-3xl text-accent-blue weather-icon"
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-3xl font-bold">
                {Math.round(currentWeather.temperature_2m)}°F
              </div>
              <div className="text-gray-400">
                {getWeatherDescription(currentWeather.weathercode)}
              </div>
            </div>
            <div className="text-right text-sm text-gray-400">
              <div className="flex items-center space-x-1">
                <SafeIcon icon={FiWind} />
                <span>{currentWeather.wind_speed_10m} mph</span>
              </div>
              <div className="flex items-center space-x-1">
                <SafeIcon icon={FiDroplet} />
                <span>{currentWeather.relative_humidity_2m}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Current Weather */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-dark-card rounded-lg p-8 border border-dark-border"
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold mb-2">New York City</h2>
            <p className="text-gray-400">Current conditions</p>
          </div>
          <SafeIcon
            icon={getWeatherIcon(currentWeather.weathercode)}
            className="text-5xl text-accent-blue weather-icon"
          />
        </div>
        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <div className="text-4xl font-bold mb-2">
              {Math.round(currentWeather.temperature_2m)}°F
            </div>
            <div className="text-xl text-gray-300 mb-4">
              {getWeatherDescription(currentWeather.weathercode)}
            </div>
          </div>
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <SafeIcon icon={FiWind} className="text-accent-blue" />
              <span className="text-gray-300">Wind: {currentWeather.wind_speed_10m} mph</span>
            </div>
            <div className="flex items-center space-x-3">
              <SafeIcon icon={FiDroplet} className="text-accent-blue" />
              <span className="text-gray-300">Humidity: {currentWeather.relative_humidity_2m}%</span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* 5-Day Forecast */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="bg-dark-card rounded-lg p-8 border border-dark-border"
      >
        <h3 className="text-xl font-bold mb-6 gradient-text">5-Day Forecast</h3>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {dailyWeather.time.slice(0, 5).map((date, index) => (
            <div
              key={date}
              className="text-center p-4 bg-dark-bg rounded-lg border border-dark-border"
            >
              <div className="text-sm text-gray-400 mb-2">
                {index === 0 ? 'Today' : format(new Date(date), 'EEE')}
              </div>
              <SafeIcon
                icon={getWeatherIcon(dailyWeather.weathercode[index])}
                className="text-2xl text-accent-blue mb-2 mx-auto weather-icon"
              />
              <div className="text-sm">
                <div className="font-semibold">
                  {Math.round(dailyWeather.temperature_2m_max[index])}°
                </div>
                <div className="text-gray-400">
                  {Math.round(dailyWeather.temperature_2m_min[index])}°
                </div>
              </div>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Weather Info */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        className="bg-primary-blue/20 border border-primary-blue/30 rounded-lg p-6"
      >
        <h4 className="text-lg font-semibold mb-2 text-accent-blue">Weather Data</h4>
        <p className="text-gray-300 text-sm">
          Weather information provided by Open-Meteo API. Data includes current conditions and forecasts for New York City (Manhattan). Updates every hour.
        </p>
      </motion.div>
    </div>
  );
}

export default WeatherWidget;